import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ProfileInfoResponse,
  usePatchExistingUser,
  useProfileInfo,
  defaultPrefix,
  useValidateLoginId,
} from 'all-in-one-shared-api';
import { UserMigrationDetails } from 'all-in-one-shared-api/dist/apis/user/usePatchExistingUser';

import { DeclarationInformationCard, WeCareFormWrapper } from '../components';
import { Spinner } from 'components';

import { useKeycloakReady } from 'hooks';

import { Form, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import { ActionType, RegistrationFlowContext } from 'contexts/registration';

import { sessionStorageService, SessionStorageKey } from 'utils/SessionStorageService';
import { verifyMobileNumber } from 'utils';

const { useForm } = Form;

const PATCH_EXISTING_USER_KEY = 'PATCH_EXISTING_USER';

const WeCareDeclarationView = () => {
  const [error, setError] = useState<string>();
  const { t /*, i18n*/ } = useTranslation();
  const [formData, setFormData] = useState<UserMigrationDetails>({});

  const initialMobileFieldValue = sessionStorage.getItem(SessionStorageKey.WecareMobile);
  const initialCountryCode = sessionStorage.getItem(SessionStorageKey.WecareCountryCode);

  const { token, ready } = useKeycloakReady();
  const navigate = useNavigate();
  const location = useLocation();

  const [form] = useForm();
  const mobileFieldValue = Form.useWatch('mobile', form) ?? '';
  const countryCodeFieldValue = Form.useWatch('countryCode', form) ?? defaultPrefix;

  const { dispatch } = useContext(RegistrationFlowContext);

  const { shouldFetch: shouldFetchProfileInfo, data: state, isLoading } = useProfileInfo('useProfileInfo', token ?? '');

  const { shouldFetch: shouldValidateLoginId } = useValidateLoginId(
    {
      key: 'ValidateLoginId',
      params: {
        loginId: formData?.loginId,
      },
    },
    token ?? '',
    formData?.loginId ?? '',
    {
      shouldRetryOnError: false,
      onSuccess: () => {
        shouldValidateLoginId(false);
        const payload = {
          countryCode: formData?.countryCode,
          dob: state?.birthDate, //absent
          email: state?.email, //absent
          localization: state?.localization,
          selfDefinedLoginId: formData?.loginId,
          memberType: state?.memberType, //absent
          mobile: mobileFieldValue,
          policyNumber: state?.policyNumber, //useless
        };
        dispatch({ type: ActionType.UpdateFormData, payload });
        sessionStorageService.set(SessionStorageKey.WecareCountryCode, formData?.countryCode ?? '');
        sessionStorageService.set(SessionStorageKey.WecareMobile, mobileFieldValue);
        navigate('/register/otp-verification', {
          state: {
            loginId: formData?.loginId,
            mobile: mobileFieldValue,
            countryCode: formData?.countryCode,
            prevPath: location.pathname,
            saveTodb: false,
          },
        });
      },
      onError: (error) => {
        shouldValidateLoginId(false);
        if (!error) {
          return setError('common.error.generic');
        }
        if (error.code === 'dapi.user.username.exists') {
          return setError('registration.error.existingUser');
        }
        setError(error.message);
      },
    },
  );

  const { shouldFetch: shouldPatch, isLoading: isPatchingExistingUser } = usePatchExistingUser(
    PATCH_EXISTING_USER_KEY,
    token ?? '',
    formData,
    {
      shouldRetryOnError: false,
      onSuccess: () => {
        if (mobileFieldValue) {
          const payload = {
            countryCode: formData?.countryCode,
            dob: state?.birthDate,
            email: state?.email,
            localization: state?.localization,
            selfDefinedLoginId: state?.loginId,
            memberType: state?.memberType,
            mobile: mobileFieldValue,
            policyNumber: state?.policyNumber,
          };
          dispatch({ type: ActionType.UpdateFormData, payload });
          sessionStorageService.set(SessionStorageKey.WecareCountryCode, formData?.countryCode ?? '');
          sessionStorageService.set(SessionStorageKey.WecareMobile, mobileFieldValue);
          navigate('/register/otp-verification', {
            state: {
              loginId: state?.loginId ?? '',
              mobile: mobileFieldValue,
              countryCode: formData?.countryCode,
              prevPath: location.pathname,
              saveTodb: true,
            },
          });
        } else {
          navigate('/register/success', {
            state: {
              shouldNavigateToHome: true,
            },
            replace: true,
          });
        }
      },
      onError: (error) => {
        if (!error) {
          return setError('common.error.generic');
        }
        if (error.code === 'auth.register.unreg.dob') {
          return setError('registration.error.invalidDob');
        } else if (error.code === 'auth.register.unreg.user') {
          return setError('registration.error.userNotFound');
        } else if (error.code === 'auth.register.unreg.email') {
          return setError('common.error.incorrectInformation');
        } else if (error.code === 'dapi.user.account.exists') {
          return setError('registration.error.existingUser');
        } else if (error.code === 'dapi.user.username.exists') {
          return setError('registration.error.existingUser');
        }
        // setSnackbarMessage(error.message);
        // Other error prompts
        setError('registration.error.invalidDob');
      },
    },
  );

  const submitVerification = async (patchParams: UserMigrationDetails) => {
    if (mobileFieldValue && !state?.loginId) {
      // Enter the mobile phone number and set the loginid
      const newFormData: UserMigrationDetails = {
        ...patchParams,
      };
      // const loginId = patchParams.loginId;
      // setloginId(loginId);
      setFormData(newFormData);
      shouldValidateLoginId(true);
    } else {
      const newFormData: UserMigrationDetails = {
        ...patchParams,
        ...(!!state?.loginId && { loginId: undefined }), // If state.loginId exists, the result is {}, and the original loginid is not overwritten
      };
      delete newFormData.mobile;
      setFormData(newFormData);
      shouldPatch(true); // api directly to the db
    }
  };

  const handleCloseSnackbar = () => {
    setError(undefined);
  };

  const onMobilePrefixChange = (prefix: string): void => {
    form.setFieldValue('countryCode', prefix);
  };

  useEffect(() => {
    shouldFetchProfileInfo(!!ready);
  }, [shouldFetchProfileInfo, ready]);

  useEffect(() => {
    form.setFieldValue('mobile', initialMobileFieldValue);
    initialCountryCode !== '' && form.setFieldValue('countryCode', initialCountryCode);
  }, [form, initialCountryCode, initialMobileFieldValue]);

  if (isLoading) return <Spinner />;

  // Note(Andy Chow): The value of Input doesn't change when initialValues change => render the form when initialValues is not empty
  return Object.keys((state as Partial<ProfileInfoResponse>) ?? {}).length !== 0 ? (
    <WeCareFormWrapper
      initialValues={state as Partial<ProfileInfoResponse>}
      onSubmit={submitVerification}
      error={error}
      handleCloseSnackbar={handleCloseSnackbar}
      isLoading={isPatchingExistingUser}
      customForm={form}
    >
      {error && (
        <Alert message={t(error)} type="error" closable onClose={handleCloseSnackbar} style={{ marginBottom: 16 }} />
      )}
      <DeclarationInformationCard
        {...(state as Partial<ProfileInfoResponse>)}
        initialPrefix={initialCountryCode && initialCountryCode !== '' ? initialCountryCode : defaultPrefix}
        isMobileValid={mobileFieldValue === '' || verifyMobileNumber(countryCodeFieldValue, mobileFieldValue)}
        onMobilePrefixChange={onMobilePrefixChange}
      />
    </WeCareFormWrapper>
  ) : (
    <></>
  );
};

export default WeCareDeclarationView;
